import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import "./LoadingSpinner.css";

/**
 * Short URL Redirect Component
 * Handles short links served from the public domain, e.g. ogestyle.com/Ab12Cd
 * (and the legacy ogestyle.com/map/Ab12Cd).
 *
 * Flow:
 * 1. The customer taps https://ogestyle.com/<code>
 * 2. The SPA loads and this component mounts
 * 3. We hand the browser straight to the backend redirect for that code
 * 4. The backend answers 302 to the real destination (Maps, a booking page, ...)
 *
 * WHY A HARD NAVIGATION AND NOT fetch():
 * The backend's 302 carries no Access-Control-Allow-Origin header, and a
 * cross-origin fetch() is not allowed to follow a redirect it cannot CORS-check -
 * the browser rejects it and reports "Failed to load link" for EVERY code, valid
 * or not. Letting the browser navigate avoids CORS entirely and keeps the
 * server-side click tracking on the backend redirect.
 *
 * An unknown or expired code is handled by the backend too: it redirects to the
 * site with ?error=invalid_link rather than dead-ending here.
 */
const ShortUrlRedirect = () => {
  const { code } = useParams();

  useEffect(() => {
    if (!code) return;

    const apiUrl =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

    // replace() so the short link does not sit in history behind the destination.
    window.location.replace(`${apiUrl}/map/${encodeURIComponent(code)}`);
  }, [code]);

  return (
    <div style={styles.container}>
      <div style={styles.loadingCard}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Redirecting...</p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  loadingCard: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    textAlign: "center",
    minWidth: "300px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    margin: "0 auto",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #1976d2",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "20px",
    color: "#666",
    fontSize: "16px",
  },
};

export default ShortUrlRedirect;
